import type { LeadsBase, LeadsReceita } from '@/types/leadsDashboard';
import { classeDelta, n, pct, setaDelta, usd, usd2 } from '../format';

/**
 * Blocos 5.1 (receita, três cards) e 5.2 (base, cinco cards).
 *
 * Ordem carrega hierarquia (§5): dinheiro primeiro, porque é o número que todo
 * mundo procura primeiro; depois a base.
 *
 * Nenhuma divisão é feita aqui — toda taxa já vem calculada do backend (§6.2).
 */

interface CardsReceitaProps {
    receita: LeadsReceita;
    rotulo: string;
    moeda: string;
    indisponivel: boolean;
}

export function CardsReceita({ receita, rotulo, moeda, indisponivel }: CardsReceitaProps) {
    if (indisponivel) {
        // §8: erro de carregamento diz o que falhou e não derruba a tela inteira.
        return (
            <div className="aviso aviso-red">
                <strong>Receita indisponível.</strong>
                <span>
                    A fonte externa de receita não respondeu. Os blocos de base e alcance
                    abaixo seguem válidos; receita, LTV e o ranking por receita ficam zerados
                    até a fonte voltar.
                </span>
            </div>
        );
    }

    return (
        <div className="kpis3">
            <div className="kpi" style={{ ['--accent' as string]: 'var(--amber)' }}>
                <div className="eyebrow">Receita total · {rotulo}</div>
                <div className="kpi-v num">{usd(receita.total, moeda)}</div>
                <div className="kpi-d">
                    <span className={classeDelta(receita.variacao_pct)}>
                        {setaDelta(receita.variacao_pct)} {pct(Math.abs(receita.variacao_pct))}
                    </span>{' '}
                    vs período anterior
                </div>
                {/* Barra dividida: proporção flow / broadcast (§5.1) */}
                <div className="split2">
                    <i style={{ width: `${receita.flow_participacao}%`, background: 'var(--teal)' }} />
                    <i style={{ width: `${receita.broadcast_participacao}%`, background: 'var(--green)' }} />
                </div>
            </div>

            <div className="kpi" style={{ ['--accent' as string]: 'var(--teal)' }}>
                <div className="eyebrow">Receita de flow · {rotulo}</div>
                <div className="kpi-v num" style={{ color: 'var(--teal)' }}>
                    {usd(receita.flow, moeda)}
                </div>
                <div className="kpi-d">
                    {pct(receita.flow_participacao, 0)} do total · automação das páginas
                </div>
            </div>

            <div className="kpi" style={{ ['--accent' as string]: 'var(--green)' }}>
                <div className="eyebrow">Receita de broadcast · {rotulo}</div>
                <div className="kpi-v num" style={{ color: 'var(--green)' }}>
                    {usd(receita.broadcast, moeda)}
                </div>
                <div className="kpi-d">
                    {pct(receita.broadcast_participacao, 0)} do total · disparo em massa
                </div>
            </div>
        </div>
    );
}

interface CardsBaseProps {
    base: LeadsBase;
    receita: LeadsReceita;
    rotulo: string;
    moeda: string;
}

export function CardsBase({ base, receita, rotulo, moeda }: CardsBaseProps) {
    return (
        <div className="kpis">
            <div className="kpi" style={{ ['--accent' as string]: 'var(--blue)' }}>
                <div className="eyebrow">Leads captados · {rotulo}</div>
                <div className="kpi-v num">{n(base.captados)}</div>
                <div className="kpi-d">
                    <span className={classeDelta(base.captados_variacao_pct)}>
                        {setaDelta(base.captados_variacao_pct)} {pct(Math.abs(base.captados_variacao_pct))}
                    </span>{' '}
                    vs anterior
                    {/* §4.1: projeção de fechamento só aparece em dia parcial. */}
                    {base.captados_projecao_fechamento != null && (
                        <> · fecha em <b className="num">{n(base.captados_projecao_fechamento)}</b></>
                    )}
                </div>
            </div>

            <div className="kpi" style={{ ['--accent' as string]: 'var(--red)' }}>
                <div className="eyebrow">Leads perdidos · erro 551</div>
                <div className="kpi-v num">{n(base.perdidos_551)}</div>
                <div className="kpi-d">
                    {/* §4.4: em perdas, subir é vermelho e cair é verde — o
                        sinal inverte em relação à captação. */}
                    <span className={classeDelta(base.perdidos_variacao_pct, false)}>
                        {setaDelta(base.perdidos_variacao_pct)} {pct(Math.abs(base.perdidos_variacao_pct))}
                    </span>{' '}
                    vs anterior · {pct(base.perdidos_sobre_captados_pct, 0)} do que entrou
                </div>
            </div>

            {/* O card de cima conta lead de qualquer idade. Este só conta quem
                entrou e saiu no mesmo dia — por dia, não no período corrido. */}
            {base.perdidos_mesmo_dia_pct != null && (
                <div className="kpi" style={{ ['--accent' as string]: 'var(--red)' }}>
                    <div className="eyebrow">Saíram no mesmo dia · {rotulo}</div>
                    <div className="kpi-v num" style={{ color: 'var(--red)' }}>
                        {pct(base.perdidos_mesmo_dia_pct)}
                    </div>
                    <div className="kpi-d">
                        <span className="num">{n(base.perdidos_mesmo_dia ?? 0)}</span> de{' '}
                        <span className="num">{n(base.captados)}</span> captados
                        {base.perdidos_mesmo_dia_pct_anterior != null && (() => {
                            const d = base.perdidos_mesmo_dia_pct - base.perdidos_mesmo_dia_pct_anterior;
                            return (
                                <>
                                    {' · '}
                                    <span className={classeDelta(d, false)}>
                                        {setaDelta(d)} {Math.abs(d).toFixed(1).replace('.', ',')} p.p.
                                    </span>
                                </>
                            );
                        })()}
                    </div>
                    <div className="kpi-bar">
                        <i style={{ width: `${Math.min(100, base.perdidos_mesmo_dia_pct)}%` }} />
                    </div>
                </div>
            )}

            <div className="kpi" style={{ ['--accent' as string]: 'var(--green)' }}>
                <div className="eyebrow">Saldo da base · {rotulo}</div>
                <div
                    className="kpi-v num"
                    style={{ color: base.saldo >= 0 ? 'var(--green)' : 'var(--red)' }}
                >
                    {base.saldo >= 0 ? '+' : '−'}{n(Math.abs(base.saldo))}
                </div>
                <div className="kpi-d">
                    retém <b>{pct(base.retencao_pct, 0)}</b> de tudo que entra
                </div>
            </div>

            <div className="kpi" style={{ ['--accent' as string]: 'var(--violet)' }}>
                <div className="eyebrow">Alcançados · {rotulo}</div>
                <div className="kpi-v num" style={{ color: 'var(--violet)' }}>
                    {n(base.alcancados_periodo)}
                </div>
                <div className="kpi-d">
                    {/* O denominador é o universo do PERÍODO, não o estoque de
                        agora. Usar `base.alcancaveis` aqui fazia a conta não
                        fechar na cara do leitor: 104 mil alcançados sobre 51
                        mil alcançáveis dando 95%. */}
                    receberam mensagem · {pct(base.cobertura_pct, 0)} dos{' '}
                    <span className="num">{n(base.alcancaveis_no_periodo)}</span>{' '}
                    alcançáveis no período
                </div>
            </div>

            {/* §5.2: o LTV é destacado visualmente e mostra a divisão inteira no
                subtítulo, para a conta ficar auditável na própria tela. */}
            <div className="kpi" data-hero="1" style={{ ['--accent' as string]: 'var(--amber)' }}>
                <div className="eyebrow">LTV por lead alcançado</div>
                <div className="kpi-v num" style={{ color: 'var(--amber)' }}>
                    {usd2(base.ltv_por_lead_alcancado, moeda)}
                </div>
                <div className="kpi-d">
                    {usd(receita.total, moeda)} ÷{' '}
                    <span className="num">{n(base.alcancados_periodo)}</span> alcançados
                </div>
            </div>
        </div>
    );
}
